import { useState, useEffect, Fragment } from 'react';
import { toast } from 'react-toastify';
import { useParams, useNavigate, useLocation } from 'react-router-dom';

import { 
  Accordion, 
  AccordionSummary, 
  AccordionDetails, 
  Box, 
  Button, 
  Typography,
  Card,
  CardContent,
  Collapse,
  IconButton,
  useTheme,
  useMediaQuery,
  Chip,
} from '@mui/material';

// Icons
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import PersonIcon from '@mui/icons-material/Person';
import HomeIcon from '@mui/icons-material/Home';
import EventIcon from '@mui/icons-material/Event';
import DescriptionIcon from '@mui/icons-material/Description';
import ArchiveIcon from '@mui/icons-material/Archive';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';

// Custom Components
import {
  Spinner,
  ClientDetailsTable,
  ClientPropertiesTable,
  BrowserTitle,
  ClientAppointmentsTable,
  ClientProposalsTable,
  ClientArchiveTable,
} from '../components';

// Utils
import messages from '../static/messages';
import { formatPhone } from '../utils/formatter';

// Services
import clientsServices from '../services/clientsServices';

// Hooks
import useAccordion from '../hooks/useAccordion';

// External variables
const blocks = ['details', 'properties', 'appointments', 'proposals'];

// Mobile Section Card Component
const MobileSectionCard = ({ title, icon: Icon, count, children, defaultExpanded = false, color = '#1976d2' }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  
  return (
    <Card variant="outlined" sx={{ mb: 2, borderColor: expanded ? color : '#e0e0e0' }}>
      <Box 
        onClick={() => setExpanded(!expanded)}
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          p: 2,
          cursor: 'pointer',
          bgcolor: expanded ? `${color}10` : 'transparent',
          '&:hover': { bgcolor: `${color}10` },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Icon sx={{ color, fontSize: 24 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
          {count !== undefined && (
            <Chip 
              label={count} 
              size="small" 
              sx={{ 
                bgcolor: color, 
                color: '#fff', 
                fontWeight: 600,
                height: 22,
                minWidth: 28,
              }} 
            />
          )}
        </Box>
        <ExpandMoreIcon 
          sx={{ 
            color: '#666',
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s',
          }} 
        />
      </Box>
      <Collapse in={expanded}>
        <CardContent sx={{ pt: 0 }}>
          {children}
        </CardContent>
      </Collapse>
    </Card>
  );
};

const Client = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [client, setClient] = useState();
  const [archive, setArchive] = useState([]);

  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { open, accordionToggle } = useAccordion(blocks);

  // Async functions
  const getClientData = async () => {
    try {
      const response = await clientsServices.getClientById(id);
      setClient(response.data);
    } catch (error) {
      if (error.response.data.status === 404) {
        navigate('/404');
      } else {
        toast.error(messages.errors.error_data_loaning);
        console.log(error);
      }
    }
  };

  // Hooks
  useEffect(() => {
    getClientData();
    accordionToggle('details');
  }, []);

  useEffect(() => {
    client?.properties.map((el) => {
      if (el.property.archiveAppointments) {
        setArchive((prevState) => [...prevState, ...el.property.archiveAppointments]);
      }
    });
  }, [client]);

  // Check for data loading
  if (!client) return <Spinner />;

  // Calculate counts
  const propertiesCount = client?.properties?.length || 0;
  const appointmentsCount = client?.appointments?.length || 0;
  const proposalsCount = client?.proposals?.length || 0;
  const archiveCount = archive?.length || 0;

  return (
    <Box component="section">
      <BrowserTitle title={`Client: ${client.fullName} | Airmaxx Pro`} />
      
      {/* Back Button: returns to Scheduled Appointments (with its filters) when we came from there */}
      <Button
        variant="text"
        onClick={() =>
          location.state?.from === 'scheduled-appointments'
            ? navigate('/scheduled-appointments', { state: location.state })
            : navigate(
                `/clients${
                  location.state ? `?page=${location?.state?.page}&per_page=${location?.state?.rowsPerPage}` : ''
                }`,
              )
        }
        startIcon={<KeyboardBackspaceIcon />}
        sx={{ mb: 2 }}
      >
        {location.state?.from === 'scheduled-appointments' ? 'Back to Scheduled Appointments' : 'Back to Clients'}
      </Button>

      {/* Mobile Header Card */}
      {isMobile && (
        <Card 
          variant="outlined" 
          sx={{ 
            mb: 3, 
            borderColor: '#1976d2',
            borderWidth: 2,
            background: 'linear-gradient(135deg, #e3f2fd 0%, #fff 100%)',
          }}
        >
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Box sx={{ 
                bgcolor: '#1976d2', 
                borderRadius: '50%', 
                width: 50, 
                height: 50,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <PersonIcon sx={{ color: '#fff', fontSize: 28 }} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1976d2' }}>
                  {client.fullName || 'No Name'}
                </Typography>
                {client.companyName && (
                  <Typography variant="body2" color="text.secondary">
                    {client.companyName}
                  </Typography>
                )}
              </Box>
            </Box>
            
            {/* Quick Contact Actions */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {client.phoneNumber && (
                <Button
                  component="a"
                  href={`tel:${client.phoneNumber}`}
                  variant="contained"
                  size="small"
                  startIcon={<PhoneIcon />}
                  sx={{ bgcolor: '#4caf50', '&:hover': { bgcolor: '#388e3c' } }}
                >
                  {formatPhone(client.phoneNumber)}
                </Button>
              )}
              {client.email && (
                <Button
                  component="a"
                  href={`mailto:${client.email}`}
                  variant="outlined"
                  size="small"
                  startIcon={<EmailIcon />}
                >
                  Email
                </Button>
              )}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Mobile View */}
      {isMobile ? (
        <Box>
          <MobileSectionCard 
            title="Client Details" 
            icon={PersonIcon} 
            defaultExpanded 
            color="#1976d2"
          >
            <ClientDetailsTable {...{ client, id, refresh: getClientData }} />
          </MobileSectionCard>

          <MobileSectionCard 
            title="Properties" 
            icon={HomeIcon} 
            count={propertiesCount}
            defaultExpanded
            color="#4caf50"
          >
            <ClientPropertiesTable properties={client?.properties} id={id} refresh={getClientData} />
          </MobileSectionCard>

          <MobileSectionCard 
            title="Appointments" 
            icon={EventIcon} 
            count={appointmentsCount}
            defaultExpanded
            color="#ff9800"
          >
            <ClientAppointmentsTable appointments={client?.appointments} />
          </MobileSectionCard>

          {archiveCount > 0 && (
            <MobileSectionCard 
              title="Archive" 
              icon={ArchiveIcon} 
              count={archiveCount}
              color="#9e9e9e"
            >
              <ClientArchiveTable appointments={archive} />
            </MobileSectionCard>
          )}

          <MobileSectionCard 
            title="Proposals" 
            icon={DescriptionIcon} 
            count={proposalsCount}
            defaultExpanded
            color="#9c27b0"
          >
            <ClientProposalsTable proposals={client?.proposals} />
          </MobileSectionCard>
        </Box>
      ) : (
        /* Desktop View - Original Accordions */
        <Box>
          {/* Client Details */}
          <Accordion defaultExpanded onChange={() => accordionToggle('details')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body1">Client Details</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <ClientDetailsTable {...{ client, id, refresh: getClientData }} />
            </AccordionDetails>
          </Accordion>

          {/* Properties */}
          <Accordion defaultExpanded onChange={() => accordionToggle('properties')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body1">Properties ({propertiesCount})</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <ClientPropertiesTable properties={client?.properties} id={id} refresh={getClientData} />
            </AccordionDetails>
          </Accordion>

          {/* Appointments */}
          <Accordion defaultExpanded onChange={() => accordionToggle('appointments')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body1">Appointments ({appointmentsCount})</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <ClientAppointmentsTable appointments={client?.appointments} />
            </AccordionDetails>
          </Accordion>

          {/* Archive */}
          {archiveCount > 0 && (
            <Accordion defaultExpanded onChange={() => accordionToggle('appointments')}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="body1">Archive ({archiveCount})</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <ClientArchiveTable appointments={archive} />
              </AccordionDetails>
            </Accordion>
          )}

          {/* Proposals */}
          <Accordion defaultExpanded onChange={() => accordionToggle('proposals')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body1">Proposals ({proposalsCount})</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <ClientProposalsTable proposals={client?.proposals} />
            </AccordionDetails>
          </Accordion>
        </Box>
      )}
    </Box>
  );
};

export default Client;
